/**
 * MessageText — renders a chat message as React elements, replacing the AngularJS nl2br
 * filter and autolinker directive in templates/chat.html.
 *
 * Office messages contain HTML: the office chat box is an Ext JS htmleditor, and the API's
 * chat endpoint wraps extra-pickup answers in <strong>. The message is parsed with
 * DOMParser, whose documents never run scripts or load images, and rebuilt from an
 * allow-list of formatting tags with every attribute dropped. Links keep only an http(s)
 * href. Other tags are unwrapped to their contents, and script-like tags are removed along
 * with their contents. Newlines and bare URLs in text become line breaks and links, as the
 * nl2br filter did.
 */

import { Fragment, useMemo } from 'react';
import { Anchor } from '@mantine/core';
import type { JSX, ReactNode } from 'react';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Ext JS htmleditor output plus the API's <strong>. <font> keeps its text but not its colour.
const FORMATTING_TAGS: Record<string, keyof JSX.IntrinsicElements> = {
  b: 'b',
  div: 'div',
  em: 'em',
  font: 'span',
  i: 'i',
  li: 'li',
  ol: 'ol',
  p: 'p',
  span: 'span',
  strong: 'strong',
  u: 'u',
  ul: 'ul',
};

const DROPPED_TAGS = new Set(['iframe', 'noscript', 'object', 'script', 'style', 'template']);

const URL_PATTERN = /(https?:\/\/\S+)/i;
const SAFE_HREF = /^https?:\/\//i;

function renderLink(href: string, children: ReactNode, key: string): ReactNode {
  return (
    <Anchor key={key} href={href} target="_blank" rel="noopener noreferrer" inherit>
      {children}
    </Anchor>
  );
}

function renderText(text: string, key: string): ReactNode[] {
  return text.split('\n').flatMap((line, i) => {
    // split() with a capture group puts the matched URLs at odd indexes
    const parts = line
      .split(URL_PATTERN)
      .map((part, j) => (j % 2 === 1 ? renderLink(part, part, `${key}-${i}-${j}`) : part));
    return i === 0 ? parts : [<br key={`${key}-${i}`} />, ...parts];
  });
}

function renderNodes(nodes: NodeListOf<ChildNode>, inLink: boolean, key: string): ReactNode[] {
  return Array.from(nodes).map((node, i) => renderNode(node, inLink, `${key}.${i}`));
}

function renderNode(node: ChildNode, inLink: boolean, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    // Anchors can't nest, so text already inside a link isn't linkified again
    return inLink ? text : <Fragment key={key}>{renderText(text, key)}</Fragment>;
  }
  if (!(node instanceof Element)) return null;

  const tag = node.tagName.toLowerCase();
  if (DROPPED_TAGS.has(tag)) return null;
  if (tag === 'br') return <br key={key} />;

  if (tag === 'a') {
    const href = node.getAttribute('href') ?? '';
    const children = renderNodes(node.childNodes, true, key);
    if (!inLink && SAFE_HREF.test(href)) return renderLink(href, children, key);
    return <Fragment key={key}>{children}</Fragment>;
  }

  const children = renderNodes(node.childNodes, inLink, key);
  const Tag = FORMATTING_TAGS[tag];
  return Tag ? <Tag key={key}>{children}</Tag> : <Fragment key={key}>{children}</Fragment>;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  html: string;
}

export function MessageText({ html }: Props) {
  // ChatPage re-renders every message on each keystroke in the compose box
  const content = useMemo(() => {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    return renderNodes(body.childNodes, false, 'm');
  }, [html]);

  return <>{content}</>;
}
